﻿// Indicator Engine — client-side compute over the live candle stream.
// Every executable study uses IndicatorRuntime and emits tail patches. Batch
// calculators remain independent numeric oracles and are never a render fallback.
// Engine drives add/remove/render lifecycle and keeps the legend on the exact
// same targetIndex model as the rendered series.
//
// No server round-trip: indicator calc runs locally. Live updates:
// terminal-app shares the chart's raw-candle array with
// the engine via setCandles (same reference), and chart.updatePrice mutates
// that array in place on every tick, then fires chart.onLiveBarUpdate →
// engine.onLiveUpdate(). One animation-frame pass advances incremental entries.
// The standalone
// appendCandle() path below stays available for callers that hand the engine
// its OWN candle array instead of sharing the chart's.
import { IndicatorSettings } from './indicator-settings.js';
import { IndicatorRenderer } from './indicator-renderer.js';
import {
    applyIndicatorOutputStyle,
    applyIndicatorStyles,
    captureIndicatorOutputStyles,
    captureIndicatorStyles,
    enforceIndicatorVisibility,
    indicatorOutputVisible,
    replaceIndicatorStyles,
    setIndicatorStyleVisibility,
} from './indicator-styles.js';
import {
    IndicatorRuntime,
    getIndicatorDefinition,
    IndicatorInputKind,
    DefaultIndicatorSource,
    IndicatorCandleField,
    IndicatorSourceKind,
    IndicatorSourceStatusReason,
    indicatorSourcesEqual,
    normalizeIndicatorSource,
    type IndicatorDefinition,
    type CandlestickData as CandlePoint,
    type IndicatorOutputAppearance,
    type IndicatorOutputMetadataValue,
    type IndicatorOutputSource,
    type IndicatorOutputStylePatch,
    type IndicatorParameters,
    type IndicatorRuntimeInput,
    type IndicatorRuntimePatch,
    type IndicatorRuntimePatchOperation,
    type IndicatorRuntimePoint,
    type IndicatorSource,
    type IndicatorSourceStatus,
} from '@stocksharp/indicators';
import type { IChartApi } from '../../core/chart-api.js';
import {
    migrateIndicatorParameters,
    migrateIndicatorType,
} from '../../indicator-parameter-migration.js';
import type { ChartPaneManager } from '../chart-pane-manager.js';
import type { IndicatorStyleSeries } from './indicator-styles.js';
import type { IndicatorPaneChart, IndicatorPaneHost } from './pane-host.js';

/** Legacy server/calc payloads belong to the chart boundary, not the indicator package API. */
interface IndicatorPoint {
    time: string | number;
    value: number | null;
}

type IndicatorLines = Record<string, IndicatorPoint[]>;
type IndicatorParams = Record<string, any>;

/**
 * The runtime as the engine holds it. One engine drives every registered definition, so the
 * per-definition input/parameter generics are erased at this boundary — the same erasure
 * IndicatorRenderer already uses on its `prepareRuntime` / `updateRuntime` parameters.
 */
type EngineRuntime = IndicatorRuntime<any, IndicatorParameters>;

/** A source scalar lifted to O=H=L=C so candlestick-input definitions can consume it. */
interface ScalarCandle {
    time: number;
    open: number | null;
    high: number | null;
    low: number | null;
    close: number | null;
    volume?: number | null;
}

/**
 * What a runtime consumes per bar: a raw candle for candlestick definitions, a lifted scalar
 * candle when a scalar source feeds one, or the bare scalar for scalar-input definitions.
 */
type EngineRuntimeValue = CandlePoint | ScalarCandle | number | null;
type EngineRuntimeInput = IndicatorRuntimeInput<EngineRuntimeValue>;

/**
 * The committed/preview split of whatever feeds one indicator — the candle window or an
 * upstream indicator output. Both producers are described by this one shape so the
 * incremental update path never has to care which of them it is walking.
 */
interface IndicatorTimeline {
    readonly committedCount: number;
    committedAt(index: number): EngineRuntimeInput;
    readonly preview: EngineRuntimeInput | undefined;
    readonly firstTime: number | null;
    readonly lastCommittedTime: number | null;
}

/** One finite upstream output sample, kept with the target index that produced it. */
interface SourceSample {
    readonly targetIndex: number;
    readonly scalar: number;
    input: EngineRuntimeInput;
}

/** Memoised upstream samples, invalidated by the upstream output revision. */
interface SourceTimelineCache {
    readonly key: string;
    revision: number;
    readonly samples: SourceSample[];
}

/** Legend readout for one bar: every output that produced a value there. */
type LegendValues = Record<string, number>;

interface LegendPoint {
    readonly time: number;
    readonly values: LegendValues;
}

/** A chart series handle the painter created and handed back to the engine. */
interface IndicatorSeriesRef extends IndicatorStyleSeries {
    applyOptions(options: object): void;
}

/** Painter-published legend binding: which series and field back one output row. */
interface IndicatorLegendSource {
    series?: IndicatorStyleSeries;
    field?: string;
    colorOption?: string;
    lineWidthOption?: string;
    lineStyleOption?: string;
    visibilityOption?: string;
}

/** One point handed to the painter: the runtime value plus the point's flat metadata. */
interface RenderedIndicatorPoint {
    time: number;
    value: number;
    [key: string]: IndicatorOutputMetadataValue;
}

/** Renderer input, one point array per output id. */
type IndicatorRenderData = Record<string, RenderedIndicatorPoint[]>;

/** The catalog row IndicatorSettings hands back — only the fields the engine reads. */
interface IndicatorCatalogParam {
    key: string;
    default?: unknown;
}

interface IndicatorCatalogEntry {
    name: string;
    pane?: string;
    measure?: string;
    serverKind?: string;
    params: readonly IndicatorCatalogParam[];
}

/** One row of the crosshair legend, as chart-legend consumes it. */
interface IndicatorLegendRow {
    id: number;
    type: string;
    name: string;
    values: Record<string, number | null>;
    colors: string[];
    paneId: string | null;
}

/**
 * The crosshair readout the legend passes through: series handle -> the point under the
 * cursor. The point itself is host-shaped — the painter names the field to read out of it —
 * so it arrives as `unknown` and is narrowed at the one place that indexes it.
 */
interface IndicatorSeriesDataLookup {
    get(series: unknown): unknown;
}

function indexableRecord(value: unknown): value is Readonly<Record<string, unknown>> {
    return typeof value === 'object' && value !== null;
}

/** A calc point that may ask to be drawn N bars back. */
interface ShiftablePoint extends IndicatorPoint {
    shift?: number;
}

/** Legacy server history payload: one row per bar carrying a value per output. */
interface IndicatorHistory {
    outputNames?: readonly string[];
    points?: readonly {
        time: string;
        shift?: number;
        values?: readonly (number | null)[];
    }[];
}

/**
 * One row of the engine's indicator table. It carries the persisted identity (id /
 * persistenceId / type / params / source / placement) plus the underscore-prefixed
 * incremental bookkeeping that keeps the rendered series, the legend table and the source
 * graph on one revision. Only fields the engine itself reads or writes are declared: the
 * painter also parks its own handles here (`_painter`, `styleSources`, …) and owns them.
 * Everything the runtime has not produced yet is optional, because add() creates the row
 * before the first reset fills that state in.
 */
interface IndicatorEntry {
    id: number;
    persistenceId: string;
    type: string;
    params: IndicatorParams;
    seriesRefs: IndicatorSeriesRef[];
    paneId: string | null;
    colors: string[];
    outputNames: string[];
    legendSources: Record<string, IndicatorLegendSource>;
    visible: boolean;
    source: IndicatorSource;
    sourceStatus: IndicatorSourceStatusReason;
    definition: IndicatorDefinition<any, any>;
    runtime: EngineRuntime;
    /** Sub-pane scale chosen automatically; `priceScaleId` overrides it when set. */
    paneScaleId: string;
    priceScaleId: string | undefined;
    _outputRevision: number;
    _outputPreviousRevision: number;
    _outputChangedFromTime: number;
    _lastOutputChanges: IndicatorRuntimePatchOperation[] | null;
    _sourceRevision: number | null;
    _runtimeFirstTime: number | null;
    _runtimePreviewTime: number | null;
    _runtimeLastCommittedTime: number | null;
    _lastRuntimePatchChangedFromTime?: number;
    _sourceTimelineCache?: SourceTimelineCache | null;
    _points?: LegendPoint[];
    _runtimeLegendTailTargets?: number[];
    _lastValues?: LegendValues | null;
    _runtimePreviewOutputTimes?: Record<string, number>;
}

export class IndicatorEngine {
    _indicators: IndicatorEntry[];
    _nextId: number;
    // Injected after construction and never re-read as a typed value: the engine calls them
    // through their own module boundaries (IndicatorRenderer / ChartPaneManager below), and
    // both are absent until the host wires them up.
    _renderer: any;
    _paneManager: IndicatorPaneHost | null;
    _symbol: string | null;
    _timeframe: number | null;
    // Read-only to the engine: the window belongs to whoever called setCandles, and the chart
    // mutates that same array in place on every tick. appendCandle is the single exception.
    _candles: readonly CandlePoint[];
    onChange: (() => void) | null;
    _renderPending: boolean | undefined;
    _retainRuntimeHistory: boolean;
    _changeListeners: Set<() => void>;

    constructor() {
        this._indicators = []; // { id, persistenceId, type, params, seriesRefs[], paneId, outputNames[] }
        this._nextId = 1;
        this._renderer = null;
        this._paneManager = null;
        this._symbol = null;
        this._timeframe = null;
        this._candles = [];
        this._retainRuntimeHistory = false;
        this._changeListeners = new Set();
        this.onChange = null;
    }

    setRenderer(renderer: IndicatorRenderer) { this._renderer = renderer; }
    setPaneManager(paneManager: IndicatorPaneHost) { this._paneManager = paneManager; }

    setSymbol(symbol: string | null) { this._symbol = symbol; }
    setTimeframe(timeframe: number | null) { this._timeframe = timeframe; }

    subscribeChange(listener: () => void): void {
        if (typeof listener !== 'function')
            throw new TypeError('sschart: indicator change listener must be a function');
        this._changeListeners.add(listener);
    }

    unsubscribeChange(listener: () => void): void {
        this._changeListeners.delete(listener);
    }

    _emitChange(): void {
        try { this.onChange?.(); } catch { /* legacy callback is an observer */ }
        for (const listener of this._changeListeners) {
            try { listener(); } catch { /* listeners are observers */ }
        }
    }

    // Keep the raw candle window as the source for incremental runtime reseeds
    // and shifted sparse timestamps.
    setCandles(
        candles: readonly CandlePoint[] | null | undefined,
        options: { rewindableTail?: boolean } = {},
    ) {
        this._candles = candles || [];
        this._retainRuntimeHistory = options.rewindableTail === true;
        // Re-render every active indicator against the fresh candle window.
        // This fires on symbol switch / timeframe change after the chart
        // pulled new candles via api.getCandles.
        for (const entry of this._orderedIndicators()) this._resetIncrementalAndRender(entry);
    }

    /// Live-tick update. terminal-app shares the chart's raw-candle array
    /// with the engine (setCandles stores that same reference), and
    /// chart.updatePrice mutates it in place — updating the last bar or pushing
    /// a new one — before firing chart.onLiveBarUpdate. So the candle window is
    /// already current here; we must NOT push again (that would duplicate the
    /// bar). We only schedule a coalesced incremental update+render so overlays
    /// (SMA/EMA/BB) and sub-pane indicators track the moving bar and new bars
    /// instead of freezing on the initial snapshot. Migrated entries produce one
    /// tail patch here. Native panes share the owner's time axis, so no separate
    /// per-pane spine bookkeeping is needed.
    onLiveUpdate() {
        this._scheduleRender();
    }

    appendCandle(candle: CandlePoint) {
        if (!this._candles.length) return;
        // The one path where the caller handed the engine its OWN array (see the note above),
        // so this is the one place allowed to write into the otherwise read-only window.
        const candles = this._candles as CandlePoint[];
        const last = candles[candles.length - 1];
        const isNewBar = last.time !== candle.time;
        if (isNewBar) {
            candles.push(candle);
        } else {
            candles[candles.length - 1] = candle;
        }
        // Live processing, coalesced per animation frame. Alpaca crypto bursts
        // through 50-200 ticks/sec; calling renderer.setData (which rebuilds
        // every chart series internally) that often makes the
        // chart visibly flicker, the legend rows shimmer, and the edit/×
        // buttons impossible to click — mouseup lands on a freshly-painted
        // canvas. RAF gates us to one repaint per frame (~60fps) without
        // changing arithmetic correctness — last call wins.
        this._scheduleRender();
    }

    /// Coalesce a render burst into a single rAF callback. Repeated forming-bar
    /// notifications collapse to one preview calculation.
    _scheduleRender() {
        if (this._renderPending) return;
        this._renderPending = true;
        const run = () => {
            this._renderPending = false;
            for (const entry of this._orderedIndicators()) this._updateIncrementalAndRender(entry);
        };
        if (typeof window !== 'undefined' && window.requestAnimationFrame) {
            window.requestAnimationFrame(run);
        } else {
            // Node / tests path: setTimeout(0). Keeps test semantics — call
            // returns synchronously, the update happens on the next task.
            setTimeout(run, 0);
        }
    }

    // targetPaneId is a genuinely optional placement hint: when the user adds an
    // indicator via a sub-pane's ＋ button it names the pane to drop into; when
    // omitted, placement is automatic (overlay on the main chart, or a measure-
    // resolved sub-pane). Absent = auto is a legitimate default, so this stays
    // optional rather than threaded through every existing call site.
    add(
        type: string,
        params: IndicatorParams | null,
        targetPaneId?: string,
        persistence: {
            persistenceId?: string;
            source?: IndicatorSource;
            priceScaleId?: string;
        } = {},
    ): IndicatorEntry | null {
        if (persistence === null || typeof persistence !== 'object' || Array.isArray(persistence))
            throw new TypeError('sschart: indicator persistence options must be an object');
        const requestedPersistenceId = persistence.persistenceId;
        if (requestedPersistenceId !== undefined
            && (typeof requestedPersistenceId !== 'string'
                || requestedPersistenceId.trim().length === 0)) {
            throw new TypeError('sschart: indicator persistence id must be a non-empty string');
        }
        const normalizedPersistenceId = requestedPersistenceId?.trim();
        if (normalizedPersistenceId !== undefined
            && this._indicators.some(entry => entry.persistenceId === normalizedPersistenceId)) {
            throw new Error(`sschart: duplicate indicator persistence id '${normalizedPersistenceId}'`);
        }
        if (persistence.priceScaleId !== undefined
            && (typeof persistence.priceScaleId !== 'string'
                || persistence.priceScaleId.trim().length === 0)) {
            throw new TypeError('sschart: indicator price scale id must be a non-empty string');
        }
        const priceScaleId = persistence.priceScaleId?.trim();
        type = migrateIndicatorType(type, params);
        const settings: IndicatorCatalogEntry | null = IndicatorSettings.getIndicator(type);
        if (!settings) return null;

        const definition = getIndicatorDefinition(settings.serverKind || type)
            || getIndicatorDefinition(type);
        if (!definition) {
            console.warn('[Indicators] no incremental runtime for', type, '— skipping');
            return null;
        }

        const id = this._nextId++;
        const persistenceId = normalizedPersistenceId || `indicator-${id}`;
        const source = persistence.source === undefined
            ? DefaultIndicatorSource
            : normalizeIndicatorSource(persistence.source);
        this._assertSourceAcyclic(persistenceId, source);
        this._assertSourceOutput(source, true);
        const mergedParams = this._mergeParams(settings, params, definition);
        let runtime: EngineRuntime;
        try {
            runtime = new IndicatorRuntime({
                definition,
                parameters: mergedParams,
                // The engine already owns the candle window. Final bars are
                // immutable by convention; only the separate preview tail is mutated.
                snapshotInput: (value: EngineRuntimeValue) => value,
            } as any);
        } catch (err) {
            console.error('[Indicators] incremental runtime init failed for', type, err);
            return null;
        }
        const entry: IndicatorEntry = {
            id, persistenceId, type,
            params: mergedParams,
            seriesRefs: [], paneId: null, colors: [], outputNames: [], legendSources: {},
            visible: true,
            source,
            sourceStatus: IndicatorSourceStatusReason.Ready,
            _outputRevision: 0,
            _outputPreviousRevision: -1,
            _outputChangedFromTime: -Infinity,
            _lastOutputChanges: null,
            _sourceRevision: null,
            definition, runtime,
            _runtimeFirstTime: null,
            _runtimePreviewTime: null,
            _runtimeLastCommittedTime: null,
            // Per-indicator price scale inside a sub-pane; 'right' (the visible axis) by default,
            // reassigned below for the 2nd+ indicator in a pane. Declared here so the type carries it.
            paneScaleId: 'right',
            // Undefined means automatic routing. An explicit id survives pane
            // rebalancing and is persisted by the workspace adapter.
            priceScaleId,
        };

        // Explicit user placement from the picker's pane selector (or a sub-pane's
        // context menu) wins over automatic placement:
        //   '__main__' -> overlay on the main chart, '__new__' -> a fresh pane,
        //   '<paneId>' -> that existing pane. Absent -> automatic (overlay vs a
        //   measure-resolved sub-pane).
        if (targetPaneId === '__main__' || targetPaneId === 'main') {
            entry.paneId = null;
        } else if (targetPaneId === '__new__' && this._paneManager) {
            const label = settings.name + ' (' + this._formatParams(mergedParams) + ')';
            entry.paneId = this._paneManager.addPane(label, null);
        } else if (targetPaneId && this._paneManager && this._paneManager.getChart(targetPaneId)) {
            entry.paneId = targetPaneId;
        } else if (settings.pane === 'separate' && this._paneManager) {
            entry.paneId = this._resolveSubPane(settings, mergedParams);
        }

        // Per-indicator price scale inside a sub-pane. The first indicator in a
        // pane keeps the visible 'right' axis; every later one gets its own
        // scale so studies with different value ranges (RSI 0..100 vs MACD
        // -2..+2) each auto-fit and overlay instead of squashing on one shared
        // scale — the multi-axis-per-area model the C# chart uses.
        if (entry.paneId) {
            const firstAutomaticInPane = !this._indicators.some(e => (
                e.paneId === entry.paneId && e.priceScaleId === undefined
            ));
            entry.paneScaleId = firstAutomaticInPane ? 'right' : `indicator:${persistenceId}`;
        }

        this._indicators.push(entry);
        this._resetCascade(entry.persistenceId);

        this._emitChange();
        return entry;
    }

    _renderData(entry: IndicatorEntry, data: IndicatorRenderData) {
        const settings = IndicatorSettings.getIndicator(entry.type);
        const chart = entry.paneId && this._paneManager ? this._paneManager.getChart(entry.paneId) : null;
        if (!entry.seriesRefs.length) {
            entry.seriesRefs = this._renderer.render(entry, data, chart, settings) || [];
            entry.colors = this._renderer.getLastColors();
            enforceIndicatorVisibility(entry);
            this._applyPaneScale(entry, chart);
        } else {
            try { this._renderer.update(entry, data, chart, settings); }
            catch (err) { console.warn('[Indicators] update failed for', entry.type, err); }
        }
    }

    _resetIncrementalAndRender(entry: IndicatorEntry) {
        const runtime = entry.runtime;
        if (!runtime) return;
        const status = this._resolveSourceStatus(entry);
        entry.sourceStatus = status.reason;
        if (!status.available) {
            this._clearRuntimeAndRender(entry);
            return;
        }
        try {
            const timeline = this._runtimeTimeline(entry);
            const inputs = Array.from(
                { length: timeline.committedCount },
                (_, index) => timeline.committedAt(index),
            );
            const preview = timeline.preview;
            let points: readonly IndicatorRuntimePoint[];
            if (this._retainRuntimeHistory) {
                runtime.reset(inputs);
                if (preview !== undefined) runtime.update(preview, false);
                points = runtime.points();
            } else {
                points = runtime.resetStreaming(inputs, preview);
            }
            entry.outputNames = runtime.outputs.map((output) => output.id);
            this._renderData(entry, this._runtimeRendererShape(entry, points));
            this._renderer.prepareRuntime(entry, runtime, points);
            this._syncRuntimeLegend(entry, points);
            entry._runtimeFirstTime = timeline.firstTime;
            entry._runtimeLastCommittedTime = timeline.lastCommittedTime;
            entry._runtimePreviewTime = preview?.time ?? null;
            entry.sourceStatus = IndicatorSourceStatusReason.Ready;
            this._markOutputsChanged(entry, -Infinity, null);
            this._rememberSourceRevision(entry);
        } catch (err) {
            entry.sourceStatus = IndicatorSourceStatusReason.Error;
            try { this._clearRuntimeAndRender(entry); } catch { /* retain the original diagnostic */ }
            console.error('[Indicators] incremental reset failed for', entry.type, err);
        }
    }

    _updateIncrementalAndRender(entry: IndicatorEntry) {
        const runtime = entry.runtime;
        if (!runtime) return;
        const status = this._resolveSourceStatus(entry);
        if (!status.available) {
            if (entry.sourceStatus !== status.reason) this._clearRuntimeAndRender(entry);
            entry.sourceStatus = status.reason;
            return;
        }
        if (entry.sourceStatus !== IndicatorSourceStatusReason.Ready) {
            if (entry.sourceStatus !== IndicatorSourceStatusReason.Error)
                this._resetIncrementalAndRender(entry);
            return;
        }
        if (this._sourceNeedsHistoricalReset(entry)) {
            this._resetIncrementalAndRender(entry);
            return;
        }
        let patchFailed = false;
        let outputChangedFromTime = Infinity;
        const outputChanges: IndicatorRuntimePatchOperation[] = [];
        const apply = (patch: IndicatorRuntimePatch) => {
            if (this._applyRuntimePatch(entry, patch)) {
                outputChangedFromTime = Math.min(
                    outputChangedFromTime,
                    entry._lastRuntimePatchChangedFromTime ?? Infinity,
                );
                outputChanges.push(...patch.operations);
                return;
            }
            patchFailed = true;
            throw new Error('indicator painter rejected a runtime tail patch');
        };
        try {
            const timeline = this._runtimeTimeline(entry);
            const preview = timeline.preview;
            const firstTime = timeline.firstTime;
            if (entry._runtimeFirstTime !== null && entry._runtimeFirstTime !== firstTime) {
                this._resetIncrementalAndRender(entry);
                return;
            }

            if (runtime.committedCount > timeline.committedCount) {
                if (!this._retainRuntimeHistory) {
                    this._resetIncrementalAndRender(entry);
                    return;
                }
                if (runtime.hasPreview) apply(runtime.discardPreview());
                while (runtime.committedCount > timeline.committedCount)
                    apply(runtime.truncateTail());
            }

            if (runtime.hasPreview) {
                const nextCommitted = runtime.committedCount < timeline.committedCount
                    ? timeline.committedAt(runtime.committedCount)
                    : undefined;
                if (nextCommitted !== undefined && nextCommitted.time === entry._runtimePreviewTime) {
                    apply(runtime.update(nextCommitted, true));
                } else if (preview?.time !== entry._runtimePreviewTime) {
                    apply(runtime.discardPreview());
                }
            }

            while (runtime.committedCount < timeline.committedCount)
                apply(runtime.update(timeline.committedAt(runtime.committedCount), true));

            if (preview !== undefined) {
                if (runtime.hasPreview && entry._runtimePreviewTime !== preview.time) {
                    this._resetIncrementalAndRender(entry);
                    return;
                }
                apply(runtime.update(preview, false));
            } else if (runtime.hasPreview) {
                apply(runtime.discardPreview());
            }
            entry._runtimeFirstTime = firstTime;
            entry._runtimeLastCommittedTime = timeline.lastCommittedTime;
            entry._runtimePreviewTime = preview?.time ?? null;
        } catch (err) {
            if (!patchFailed)
                console.error('[Indicators] incremental update failed for', entry.type, err);
            this._resetIncrementalAndRender(entry);
            return;
        }
        if (Number.isFinite(outputChangedFromTime))
            this._markOutputsChanged(entry, outputChangedFromTime, outputChanges);
        this._rememberSourceRevision(entry);
        if (!this._retainRuntimeHistory) runtime.compactHistory();
    }

    _applyRuntimePatch(entry: IndicatorEntry, patch: IndicatorRuntimePatch) {
        let applied = false;
        try { applied = this._renderer.updateRuntime(entry, patch, entry.runtime); }
        catch (err) { console.warn('[Indicators] runtime painter update failed for', entry.type, err); }
        if (!applied) return false;
        const changedFromTime = this._runtimePatchChangedFromTime(entry, patch);
        entry._lastRuntimePatchChangedFromTime = changedFromTime;
        const legendApplied = this._applyRuntimeLegendPatch(entry, patch);
        if (legendApplied) this._refreshRuntimePreviewOutputTimes(entry);
        return legendApplied;
    }

    _runtimeInput(entry: IndicatorEntry, candle: CandlePoint): EngineRuntimeInput {
        const time = this._toSec(candle.time);
        const scalarInput = entry.definition?.input?.kind === IndicatorInputKind.Scalar;
        const source: IndicatorSource = entry.source || DefaultIndicatorSource;
        if (source.kind === IndicatorSourceKind.Candles) {
            return {
                time,
                value: scalarInput ? this._candleFieldValue(candle, IndicatorCandleField.Close) : candle,
            };
        }
        const scalar = source.kind === IndicatorSourceKind.CandleField
            ? this._candleFieldValue(candle, source.field)
            : this._indicatorOutputAt(source.indicatorId, source.outputId, time);
        return {
            time,
            value: scalarInput ? scalar : this._scalarCandle(candle, time, scalar, source),
        };
    }

    _runtimeTimeline(entry: IndicatorEntry): IndicatorTimeline {
        const source: IndicatorSource = entry.source || DefaultIndicatorSource;
        if (source.kind !== IndicatorSourceKind.IndicatorOutput) {
            const committedCount = Math.max(0, this._candles.length - 1);
            const previewCandle = this._candles.at(-1);
            return {
                committedCount,
                committedAt: index => this._runtimeInput(entry, this._candles[index]),
                preview: previewCandle === undefined
                    ? undefined : this._runtimeInput(entry, previewCandle),
                firstTime: this._candles.length > 0
                    ? this._toSec(this._candles[0].time) : null,
                lastCommittedTime: committedCount > 0
                    ? this._toSec(this._candles[committedCount - 1].time) : null,
            };
        }

        const upstream = this._indicators.find(candidate => (
            candidate.persistenceId === source.indicatorId
        ));
        const samples = upstream === undefined ? [] : this._sourceSamples(entry, upstream, source);
        // `lastSample !== undefined` is exactly the old `samples.length > 0`, but it also carries
        // the tail sample forward so the preview below needs no second lookup.
        const lastSample = samples.at(-1);
        const hasPreview = lastSample !== undefined
            && upstream?._runtimePreviewOutputTimes?.[source.outputId]
                === lastSample.input.time;
        const committedCount = samples.length - (hasPreview ? 1 : 0);
        return {
            committedCount,
            committedAt: index => samples[index].input,
            preview: hasPreview && lastSample !== undefined ? lastSample.input : undefined,
            firstTime: samples[0]?.input.time ?? null,
            lastCommittedTime: committedCount > 0
                ? samples[committedCount - 1].input.time : null,
        };
    }

    _sourceSamples(
        entry: IndicatorEntry,
        upstream: IndicatorEntry,
        source: IndicatorOutputSource,
    ): SourceSample[] {
        const key = `${source.indicatorId}\u0000${source.outputId}`;
        let cache = entry._sourceTimelineCache;
        if (!cache || cache.key !== key) cache = null;
        if (cache && cache.revision !== upstream._outputRevision) {
            if (cache.revision === upstream._outputPreviousRevision
                && Array.isArray(upstream._lastOutputChanges)) {
                this._applySourceSampleChanges(entry, cache.samples, source, upstream._lastOutputChanges);
                cache.revision = upstream._outputRevision;
            } else cache = null;
        }
        if (!cache) {
            const samples: SourceSample[] = [];
            const points = upstream._points || [];
            const targets = upstream._runtimeLegendTailTargets || [];
            for (let index = 0; index < points.length; index++) {
                const point = points[index];
                const scalar = point.values?.[source.outputId];
                if (typeof scalar !== 'number' || !Number.isFinite(scalar)) continue;
                samples.push(this._sourceSample(entry, source, targets[index], point.time, scalar));
            }
            cache = { key, revision: upstream._outputRevision, samples };
            entry._sourceTimelineCache = cache;
        }
        const last = cache.samples.at(-1);
        if (last) {
            last.input = this._runtimeInputFromScalar(
                entry,
                this._candleAtTime(last.input.time),
                last.input.time,
                last.scalar,
                source,
            );
        }
        return cache.samples;
    }

    _applySourceSampleChanges(
        entry: IndicatorEntry,
        samples: SourceSample[],
        source: IndicatorOutputSource,
        changes: readonly IndicatorRuntimePatchOperation[],
    ) {
        for (const operation of changes) {
            if (operation.outputId !== source.outputId) continue;
            const index = samples.findIndex(sample => sample.targetIndex === operation.targetIndex);
            const point = operation.point;
            if (operation.operation === 'remove' || !point || point.time === null
                || typeof point.value !== 'number' || !Number.isFinite(point.value)) {
                if (index >= 0) samples.splice(index, 1);
                continue;
            }
            const sample = this._sourceSample(
                entry,
                source,
                point.targetIndex,
                point.time,
                point.value,
            );
            if (index >= 0) samples[index] = sample;
            else {
                samples.push(sample);
                samples.sort((left, right) => left.targetIndex - right.targetIndex);
            }
        }
    }

    _sourceSample(
        entry: IndicatorEntry,
        source: IndicatorOutputSource,
        targetIndex: number,
        time: number,
        scalar: number,
    ): SourceSample {
        const candle = this._candleAtTime(time);
        return {
            targetIndex,
            scalar,
            input: this._runtimeInputFromScalar(entry, candle, time, scalar, source),
        };
    }

    _runtimeInputFromScalar(
        entry: IndicatorEntry,
        candle: CandlePoint | null,
        time: number,
        scalar: number | null,
        source: IndicatorSource,
    ): EngineRuntimeInput {
        const scalarInput = entry.definition?.input?.kind === IndicatorInputKind.Scalar;
        return {
            time,
            value: scalarInput ? scalar : this._scalarCandle(candle, time, scalar, source),
        };
    }

    _candleAtTime(time: number): CandlePoint | null {
        let low = 0;
        let high = this._candles.length - 1;
        while (low <= high) {
            const middle = (low + high) >> 1;
            const candle = this._candles[middle];
            const candleTime = this._toSec(candle.time);
            if (candleTime === time) return candle;
            if (candleTime < time) low = middle + 1;
            else high = middle - 1;
        }
        return null;
    }

    _scalarCandle(
        candle: CandlePoint | null,
        time: number,
        scalar: number | null,
        source: IndicatorSource,
    ): ScalarCandle {
        const value = typeof scalar === 'number' && Number.isFinite(scalar) ? scalar : null;
        const volume = source.kind === IndicatorSourceKind.CandleField
            && source.field === IndicatorCandleField.Volume
            ? value
            : candle?.volume;
        return {
            time,
            open: value,
            high: value,
            low: value,
            close: value,
            ...(volume === undefined ? {} : { volume }),
        };
    }

    _candleFieldValue(candle: CandlePoint | null, field: IndicatorCandleField): number | null {
        const finite = (value: unknown) => (
            typeof value === 'number' && Number.isFinite(value) ? value : null
        );
        if (field === IndicatorCandleField.Open) return finite(candle?.open);
        if (field === IndicatorCandleField.High) return finite(candle?.high);
        if (field === IndicatorCandleField.Low) return finite(candle?.low);
        if (field === IndicatorCandleField.Close) return finite(candle?.close);
        if (field === IndicatorCandleField.Volume) return finite(candle?.volume);
        const open = finite(candle?.open);
        const high = finite(candle?.high);
        const low = finite(candle?.low);
        const close = finite(candle?.close);
        if (field === IndicatorCandleField.Median)
            return high === null || low === null ? null : (high + low) / 2;
        if (field === IndicatorCandleField.Typical)
            return high === null || low === null || close === null ? null : (high + low + close) / 3;
        return open === null || high === null || low === null || close === null
            ? null : (open + high + low + close) / 4;
    }

    _indicatorOutputAt(indicatorId: string, outputId: string, time: number): number | null {
        const entry = this._indicators.find(candidate => candidate.persistenceId === indicatorId);
        const points = entry?._points || [];
        let low = 0;
        let high = points.length - 1;
        while (low <= high) {
            const middle = (low + high) >> 1;
            const point = points[middle];
            if (point.time === time) {
                const value = point.values?.[outputId];
                return typeof value === 'number' && Number.isFinite(value) ? value : null;
            }
            if (point.time < time) low = middle + 1;
            else high = middle - 1;
        }
        return null;
    }

    _clearRuntimeAndRender(entry: IndicatorEntry) {
        const runtime = entry.runtime;
        let points: readonly IndicatorRuntimePoint[];
        if (this._retainRuntimeHistory) {
            runtime.reset([]);
            points = runtime.points();
        } else {
            points = runtime.resetStreaming([]);
        }
        entry.outputNames = runtime.outputs.map((output) => output.id);
        this._renderData(entry, this._runtimeRendererShape(entry, points));
        this._renderer.prepareRuntime(entry, runtime, points);
        this._syncRuntimeLegend(entry, points);
        entry._runtimeFirstTime = null;
        entry._runtimeLastCommittedTime = null;
        entry._runtimePreviewTime = null;
        this._markOutputsChanged(entry, -Infinity, null);
        this._rememberSourceRevision(entry);
    }

    _runtimeRendererShape(
        entry: IndicatorEntry,
        points: readonly IndicatorRuntimePoint[] = entry.runtime.points(),
    ): IndicatorRenderData {
        const data: IndicatorRenderData = {};
        for (const output of entry.outputNames) data[output] = [];
        for (const point of points) {
            const output = data[point.outputId];
            if (output && point.time !== null)
                output.push({ ...point.metadata, time: point.time, value: point.value });
        }
        return data;
    }

    _syncRuntimeLegend(
        entry: IndicatorEntry,
        points: readonly IndicatorRuntimePoint[] = entry.runtime.points(),
    ) {
        if (entry.outputNames.length === 1) {
            const outputId = entry.outputNames[0];
            entry._points = [];
            entry._runtimeLegendTailTargets = [];
            for (const point of points) {
                if (point.outputId !== outputId || point.time === null) continue;
                entry._points.push({ time: point.time, values: { [outputId]: point.value } });
                entry._runtimeLegendTailTargets.push(point.targetIndex);
            }
            entry._lastValues = entry._points.length
                ? entry._points[entry._points.length - 1].values
                : null;
            this._refreshRuntimePreviewOutputTimes(entry, points);
            return;
        }

        const byTarget = new Map<number, { time: number; values: LegendValues }>();
        for (const point of points) {
            if (point.time === null) continue;
            let row = byTarget.get(point.targetIndex);
            if (!row) {
                row = { time: point.time, values: {} };
                byTarget.set(point.targetIndex, row);
            }
            row.time = point.time;
            row.values[point.outputId] = point.value;
        }
        const ordered = [...byTarget.entries()].sort((left, right) => left[0] - right[0]);
        entry._points = ordered.map(([, row]) => ({ time: row.time, values: row.values }));
        entry._runtimeLegendTailTargets = ordered.map(([targetIndex]) => targetIndex);
        entry._lastValues = entry._points.length
            ? entry._points[entry._points.length - 1].values
            : null;
        this._refreshRuntimePreviewOutputTimes(entry, points);
    }

    _runtimePatchChangedFromTime(entry: IndicatorEntry, patch: IndicatorRuntimePatch): number {
        let changedFromTime = Infinity;
        for (const operation of patch.operations) {
            const point = operation.point;
            if (point?.time !== null && point?.time !== undefined)
                changedFromTime = Math.min(changedFromTime, point.time);
            const index = entry._runtimeLegendTailTargets?.lastIndexOf(operation.targetIndex) ?? -1;
            const previousTime = index >= 0 ? entry._points?.[index]?.time : undefined;
            if (previousTime !== undefined)
                changedFromTime = Math.min(changedFromTime, previousTime);
        }
        return changedFromTime;
    }

    _refreshRuntimePreviewOutputTimes(
        entry: IndicatorEntry,
        points: readonly IndicatorRuntimePoint[] = entry.runtime.points(),
    ) {
        const times: Record<string, number> = {};
        if (entry.runtime.hasPreview) {
            for (const point of points) {
                if (point.time !== null && point.sourceIndex === entry.runtime.committedCount)
                    times[point.outputId] = point.time;
            }
        }
        entry._runtimePreviewOutputTimes = times;
    }

    _applyRuntimeLegendPatch(entry: IndicatorEntry, patch: IndicatorRuntimePatch): boolean {
        const points = entry._points || (entry._points = []);
        const targets = entry._runtimeLegendTailTargets
            || (entry._runtimeLegendTailTargets = []);
        for (const operation of patch.operations) {
            const lastIndex = points.length - 1;
            const lastTarget = targets.length ? targets[targets.length - 1] : -1;
            if (operation.operation === 'remove') {
                if (operation.targetIndex !== lastTarget || lastIndex < 0) return false;
                const current = points[lastIndex];
                const values = { ...current.values };
                delete values[operation.outputId];
                if (Object.keys(values).length > 0) {
                    points[lastIndex] = { time: current.time, values };
                } else {
                    points.pop();
                    targets.pop();
                }
                continue;
            }

            const point = operation.point;
            if (!point || point.time === null) return false;
            if (point.targetIndex === lastTarget && lastIndex >= 0) {
                const current = points[lastIndex];
                if (current.time !== point.time) return false;
                points[lastIndex] = {
                    time: point.time,
                    values: { ...current.values, [point.outputId]: point.value },
                };
            } else {
                if (point.targetIndex <= lastTarget) return false;
                points.push({ time: point.time, values: { [point.outputId]: point.value } });
                targets.push(point.targetIndex);
            }
        }
        entry._lastValues = points.length ? points[points.length - 1].values : null;
        return true;
    }

    /// Route an indicator's series onto its own sub-pane scale (see add()). The
    /// first indicator in a pane stays on 'right' (the visible axis, drawn by the
    /// engine); every later one moves to a private scale that auto-fits its own
    /// range, with the same margins so each study uses the full pane height. The
    /// engine draws axes only for 'right'/'left', so these extra scales stay
    /// invisible — they exist purely to keep the overlays from squashing.
    _applyPaneScale(entry: IndicatorEntry, chart: IndicatorPaneChart | null) {
        const sid = entry.priceScaleId || entry.paneScaleId;
        if (!sid) return;
        for (const s of entry.seriesRefs) {
            try { s.applyOptions({ priceScaleId: sid }); } catch { }
        }
        if (!chart || sid === 'right') return;
        try { chart.priceScale(sid).applyOptions({ scaleMargins: { top: 0.1, bottom: 0.1 } }); } catch { }
    }

    /// Materialise a `[{time, values:{k:v, ...}}]` table from the calc output
    /// so the legend can read all outputs at a given bar in O(log N) (binary
    /// search by time). Time is converted to seconds-since-epoch — matches
    /// what the chart runtime and our chart-widget already use.
    _buildLegendPoints(entry: IndicatorEntry, data: IndicatorPoint[] | IndicatorLines): LegendPoint[] {
        const out: LegendPoint[] = [];
        if (Array.isArray(data)) {
            for (const p of data) {
                if (p.value == null) continue;
                out.push({ time: this._toSec(p.time), values: { value: Number(p.value) } });
            }
        } else {
            const keys = Object.keys(data);
            // Merge by timestamp, not array index. After null stripping sparse
            // outputs (Fractals up/down) have different lengths and timestamps.
            const byTime = new Map<number, LegendValues>();
            for (const key of keys) {
                for (const point of data[key] || []) {
                    if (point?.value == null) continue;
                    const time = this._toSec(point.time);
                    let values = byTime.get(time);
                    if (!values) byTime.set(time, values = {});
                    values[key] = Number(point.value);
                }
            }
            for (const [time, values] of byTime) out.push({ time, values });
            out.sort((a, b) => a.time - b.time);
        }
        return out;
    }

    _toSec(time: string | number): number {
        if (typeof time === 'number') return time;
        const p = Date.parse(time);
        return isFinite(p) ? Math.floor(p / 1000) : 0;
    }

    _orderedIndicators(): IndicatorEntry[] {
        const byId = new Map(this._indicators.map(
            (entry): [string, IndicatorEntry] => [entry.persistenceId, entry],
        ));
        const visiting = new Set<string>();
        const visited = new Set<string>();
        const ordered: IndicatorEntry[] = [];
        // The return annotation is what lets `visit` recurse into itself below.
        const visit = (entry: IndicatorEntry): void => {
            if (visited.has(entry.persistenceId)) return;
            if (visiting.has(entry.persistenceId))
                throw new Error('sschart: indicator source graph contains a cycle');
            visiting.add(entry.persistenceId);
            const source: IndicatorSource = entry.source || DefaultIndicatorSource;
            if (source.kind === IndicatorSourceKind.IndicatorOutput) {
                const upstream = byId.get(source.indicatorId);
                if (upstream) visit(upstream);
            }
            visiting.delete(entry.persistenceId);
            visited.add(entry.persistenceId);
            ordered.push(entry);
        };
        for (const entry of this._indicators) visit(entry);
        return ordered;
    }

    _resetCascade(rootPersistenceId: string) {
        const affected = new Set([rootPersistenceId]);
        for (const entry of this._orderedIndicators()) {
            const source: IndicatorSource = entry.source || DefaultIndicatorSource;
            if (source.kind === IndicatorSourceKind.IndicatorOutput
                && affected.has(source.indicatorId)) affected.add(entry.persistenceId);
            if (affected.has(entry.persistenceId)) this._resetIncrementalAndRender(entry);
        }
    }

    _resetDependents(removedPersistenceId: string) {
        const affected = new Set([removedPersistenceId]);
        for (const entry of this._orderedIndicators()) {
            const source: IndicatorSource = entry.source || DefaultIndicatorSource;
            if (source.kind !== IndicatorSourceKind.IndicatorOutput
                || !affected.has(source.indicatorId)) continue;
            affected.add(entry.persistenceId);
            this._resetIncrementalAndRender(entry);
        }
    }

    _resolveSourceStatus(entry: IndicatorEntry): IndicatorSourceStatus {
        const source: IndicatorSource = entry.source || DefaultIndicatorSource;
        if (source.kind !== IndicatorSourceKind.IndicatorOutput) {
            return Object.freeze({
                source,
                available: true,
                reason: IndicatorSourceStatusReason.Ready,
            });
        }
        const upstream = this._indicators.find(candidate => (
            candidate.persistenceId === source.indicatorId
        ));
        if (!upstream) return Object.freeze({
            source,
            available: false,
            reason: IndicatorSourceStatusReason.MissingIndicator,
        });
        const outputs = upstream.outputNames?.length
            ? upstream.outputNames
            : upstream.runtime?.outputs?.map(output => output.id) || [];
        if (!outputs.includes(source.outputId)) return Object.freeze({
            source,
            available: false,
            reason: IndicatorSourceStatusReason.MissingOutput,
        });
        if (upstream.sourceStatus !== IndicatorSourceStatusReason.Ready) {
            return Object.freeze({
                source,
                available: false,
                reason: IndicatorSourceStatusReason.UpstreamUnavailable,
            });
        }
        return Object.freeze({
            source,
            available: true,
            reason: IndicatorSourceStatusReason.Ready,
        });
    }

    _sourceEntry(entry: IndicatorEntry): IndicatorEntry | null {
        const source: IndicatorSource = entry.source || DefaultIndicatorSource;
        return source.kind === IndicatorSourceKind.IndicatorOutput
            ? this._indicators.find(candidate => candidate.persistenceId === source.indicatorId) || null
            : null;
    }

    _sourceNeedsHistoricalReset(entry: IndicatorEntry): boolean {
        const upstream = this._sourceEntry(entry);
        if (!upstream || entry._sourceRevision === upstream._outputRevision) return false;
        // Null until the first runtime pass commits a bar: with nothing committed there is no
        // history for the upstream to have invalidated.
        const lastCommittedTime = entry._runtimeLastCommittedTime;
        return lastCommittedTime !== null
            && upstream._outputChangedFromTime <= lastCommittedTime;
    }

    _rememberSourceRevision(entry: IndicatorEntry) {
        const upstream = this._sourceEntry(entry);
        entry._sourceRevision = upstream?._outputRevision ?? null;
    }

    _markOutputsChanged(
        entry: IndicatorEntry,
        time: number,
        changes: IndicatorRuntimePatchOperation[] | null,
    ) {
        const previous = entry._outputRevision || 0;
        entry._outputPreviousRevision = previous;
        entry._outputRevision = previous + 1;
        entry._outputChangedFromTime = time;
        entry._lastOutputChanges = changes;
    }

    _assertSourceAcyclic(ownerPersistenceId: string, source: IndicatorSource) {
        let current = source;
        const visited = new Set<string>();
        while (current.kind === IndicatorSourceKind.IndicatorOutput) {
            // `current` is reassigned by the loop, so the kind narrowing does not reach into the
            // closure below; bind the already-narrowed value to a const the closure can capture.
            const outputSource = current;
            if (outputSource.indicatorId === ownerPersistenceId)
                throw new RangeError('sschart: indicator source dependency cannot contain a cycle');
            if (visited.has(outputSource.indicatorId))
                throw new Error('sschart: existing indicator source graph contains a cycle');
            visited.add(outputSource.indicatorId);
            const upstream = this._indicators.find(candidate => (
                candidate.persistenceId === outputSource.indicatorId
            ));
            if (!upstream) return;
            current = upstream.source || DefaultIndicatorSource;
        }
    }

    _assertSourceOutput(source: IndicatorSource, allowMissingIndicator: boolean) {
        if (source.kind !== IndicatorSourceKind.IndicatorOutput) return;
        const upstream = this._indicators.find(candidate => (
            candidate.persistenceId === source.indicatorId
        ));
        if (!upstream) {
            if (allowMissingIndicator) return;
            throw new RangeError(
                `sschart: indicator source '${source.indicatorId}' is unavailable`,
            );
        }
        const outputs = upstream.outputNames?.length
            ? upstream.outputNames
            : upstream.runtime?.outputs?.map(output => output.id) || [];
        if (!outputs.includes(source.outputId)) {
            throw new RangeError(
                `sschart: indicator source output '${source.outputId}' is unavailable`,
            );
        }
    }

    remove(id: number) {
        const idx = this._indicators.findIndex(e => e.id === id);
        if (idx < 0) return;
        const entry = this._indicators[idx];
        const persistenceId = entry.persistenceId;
        this._removeEntry(entry);
        this._resetDependents(persistenceId);
        this._emitChange();
    }

    _removeEntry(entry: IndicatorEntry) {
        // The painter may own resources in addition to its returned series, so
        // let the renderer notify it before removing the chart primitives.
        if (this._renderer) this._renderer.removeSeries(entry);
        const i = this._indicators.indexOf(entry);
        if (i >= 0) this._indicators.splice(i, 1);
        // Close the sub-pane only if no other active indicator is drawing
        // into it — multiple Percent-scale indicators (RSI + Stochastic,
        // etc.) now share one pane, so we must not drop it while siblings
        // are still rendering.
        if (entry.paneId && this._paneManager) {
            const stillUsed = this._indicators.some(e => e.paneId === entry.paneId);
            if (!stillUsed) {
                try { this._paneManager.removePane(entry.paneId); } catch { }
            } else {
                this._rebalancePaneScales(entry.paneId);
            }
        }
    }

    move(id: number, targetPaneId: string): boolean {
        const entry = this._indicators.find(item => item.id === id);
        if (!entry) return false;
        if (typeof targetPaneId !== 'string' || targetPaneId.trim().length === 0)
            throw new TypeError('sschart: indicator target pane id must be non-empty');
        const target = targetPaneId.trim();
        const toMain = target === '__main__' || target === 'main';
        const previousPaneId = entry.paneId;
        if (toMain && previousPaneId === null) return false;
        if (!toMain && target !== '__new__' && previousPaneId === target) return false;
        if (!this._renderer || typeof this._renderer.moveSeries !== 'function')
            throw new Error('sschart: indicator renderer cannot move series');

        let nextPaneId: string | null = null;
        let targetChart: IndicatorPaneChart | null = null;
        let createdPaneId: string | null = null;
        let restoredPaneId: string | null = null;
        if (!toMain) {
            if (!this._paneManager)
                throw new Error('sschart: indicator pane manager is unavailable');
            if (target === '__new__') {
                const settings = IndicatorSettings.getIndicator(entry.type);
                const label = settings.name + ' (' + this._formatParams(entry.params) + ')';
                createdPaneId = this._paneManager.addPane(label, settings.measure || null);
                if (!createdPaneId) throw new Error('sschart: indicator pane could not be created');
                nextPaneId = createdPaneId;
            } else {
                nextPaneId = target;
            }
            targetChart = this._paneManager.getChart(nextPaneId);
            if (!targetChart && typeof this._paneManager.restorePane === 'function') {
                restoredPaneId = this._paneManager.restorePane(nextPaneId);
                if (restoredPaneId === nextPaneId)
                    targetChart = this._paneManager.getChart(nextPaneId);
            }
            if (!targetChart) {
                if (createdPaneId) this._paneManager.removePane(createdPaneId);
                throw new Error(`sschart: indicator target pane '${nextPaneId}' is unavailable`);
            }
        }

        try {
            this._renderer.moveSeries(entry, targetChart);
        } catch (error) {
            if (createdPaneId) this._paneManager?.removePane(createdPaneId);
            if (restoredPaneId) this._paneManager?.removePane(restoredPaneId);
            throw error;
        }
        entry.paneId = nextPaneId;
        this._rebalancePaneScales(previousPaneId);
        this._rebalancePaneScales(nextPaneId);
        if (previousPaneId && this._paneManager
            && !this._indicators.some(item => item.paneId === previousPaneId)) {
            this._paneManager.removePane(previousPaneId);
        }
        this._emitChange();
        return true;
    }

    /** Selects an explicit price scale; null returns the indicator to automatic routing. */
    setScale(id: number, priceScaleId: string | null): boolean {
        const entry = this._indicators.find(item => item.id === id);
        if (!entry) return false;
        if (priceScaleId !== null
            && (typeof priceScaleId !== 'string' || priceScaleId.trim().length === 0)) {
            throw new TypeError('sschart: indicator price scale id must be non-empty or null');
        }
        const next = priceScaleId === null ? undefined : priceScaleId.trim();
        if (entry.priceScaleId === next) return false;
        entry.priceScaleId = next;
        this._rebalancePaneScales(entry.paneId);
        this._emitChange();
        return true;
    }

    _rebalancePaneScales(paneId: string | null) {
        const entries = this._indicators.filter(entry => entry.paneId === paneId);
        const chart = paneId && this._paneManager ? this._paneManager.getChart(paneId) : null;
        let automaticIndex = 0;
        for (const entry of entries) {
            if (entry.priceScaleId === undefined) {
                entry.paneScaleId = paneId && automaticIndex > 0
                    ? `indicator:${entry.persistenceId}`
                    : 'right';
                automaticIndex++;
            }
            this._applyPaneScale(entry, chart);
        }
    }

    removeAll() {
        const entries = this._indicators.slice();
        for (const entry of entries) this.remove(entry.id);
    }

    // Re-seeds every active local runtime after a timeframe/source change.
    // Series identity, pane placement, persistence ids and user styles stay intact.
    async resubscribeAll() {
        for (const entry of this._orderedIndicators()) this._resetIncrementalAndRender(entry);
    }

    // Called by indicator-dialog's edit-save path: keep the row but re-fetch
    // history with updated parameters. Simplest correct path is to drop the
    // old subscription and add a fresh one with the same type — the server
    // keys each IIndicator instance per subscription so new params mean new
    // compute state anyway.
    replaceParams(id: number, newParams: IndicatorParams) {
        const idx = this._indicators.findIndex(e => e.id === id);
        if (idx < 0) return;
        const entry = this._indicators[idx];
        const type = entry.type;
        const settings = IndicatorSettings.getIndicator(type);
        const patch = migrateIndicatorParameters(entry.definition, newParams);
        const merged = this._mergeParams(
            settings,
            { ...entry.params, ...patch },
            entry.definition,
        );
        const styles = captureIndicatorStyles(entry);
        const targetPaneId = entry.paneId || '__main__';
        const persistenceId = entry.persistenceId;
        const source = entry.source || DefaultIndicatorSource;
        const priceScaleId = entry.priceScaleId;
        const visible = entry.visible !== false;
        this.remove(id);
        let restoredPaneId: string | null = null;
        if (targetPaneId !== '__main__' && this._paneManager
            && !this._paneManager.getChart(targetPaneId)
            && typeof this._paneManager.restorePane === 'function') {
            const restored = this._paneManager.restorePane(targetPaneId);
            if (restored === targetPaneId) restoredPaneId = restored;
        }
        const replacement = this.add(type, merged, targetPaneId, {
            persistenceId,
            source,
            priceScaleId,
        });
        if (replacement) {
            // Parameter replacement recreates the runtime, but it must not reorder
            // the stable workspace row. add() appends, so put the replacement back
            // at the exact previous position before publishing the final state.
            const appendedIndex = this._indicators.indexOf(replacement);
            if (appendedIndex >= 0 && appendedIndex !== idx) {
                this._indicators.splice(appendedIndex, 1);
                this._indicators.splice(Math.min(idx, this._indicators.length), 0, replacement);
                this._rebalancePaneScales(replacement.paneId);
            }
            applyIndicatorStyles(replacement, styles);
            if (!visible) setIndicatorStyleVisibility(replacement, false);
            // add() notifies before restored styles/order are applied. Publish one
            // final coherent snapshot for legends, editors and persistence.
            this._emitChange();
        } else if (restoredPaneId && this._paneManager) {
            this._paneManager.removePane(restoredPaneId);
        }
        return replacement;
    }

    getIndicators(): IndicatorEntry[] { return this._indicators.slice(); }

    /** Rebinds one runtime and every transitive dependent in graph order. */
    setSource(id: number, value: IndicatorSource): boolean {
        const entry = this._indicators.find(candidate => candidate.id === id);
        if (!entry) return false;
        const source = normalizeIndicatorSource(value);
        if (indicatorSourcesEqual(entry.source || DefaultIndicatorSource, source)) return false;
        this._assertSourceAcyclic(entry.persistenceId, source);
        this._assertSourceOutput(source, false);
        entry.source = source;
        this._resetCascade(entry.persistenceId);
        this._emitChange();
        return true;
    }

    getSourceStatus(id: number): IndicatorSourceStatus | null {
        const entry = this._indicators.find(candidate => candidate.id === id);
        if (!entry) return null;
        if (entry.sourceStatus === IndicatorSourceStatusReason.Error) {
            return Object.freeze({
                source: entry.source || DefaultIndicatorSource,
                available: false,
                reason: IndicatorSourceStatusReason.Error,
            });
        }
        return this._resolveSourceStatus(entry);
    }

    /** Applies one output's visual options without rebuilding its runtime or series. */
    setOutputStyle(id: number, outputId: string, patch: IndicatorOutputStylePatch): boolean {
        const entry = this._indicators.find(candidate => candidate.id === id);
        if (!entry) return false;
        const changed = applyIndicatorOutputStyle(entry, outputId, patch);
        if (changed) this._emitChange();
        return changed;
    }

    /** Hides all painter-owned series while retaining computation and object identity. */
    setVisible(id: number, visible: boolean): boolean {
        if (typeof visible !== 'boolean')
            throw new TypeError('sschart: indicator visible must be boolean');
        const entry = this._indicators.find(candidate => candidate.id === id);
        if (!entry) return false;
        const changed = setIndicatorStyleVisibility(entry, visible);
        if (changed) this._emitChange();
        return changed;
    }

    /** Returns a detached snapshot keyed by the painter's stable style ids. */
    getStyles(id: number): Readonly<Record<string, Readonly<Record<string, unknown>>>> | null {
        const entry = this._indicators.find(candidate => candidate.id === id);
        if (!entry) return null;
        const styles = captureIndicatorStyles(entry);
        return Object.freeze(Object.fromEntries(Object.entries(styles).map(([key, options]) => [
            key,
            Object.freeze({ ...options }),
        ])));
    }

    /** Returns effective editor fields keyed by semantic output id. */
    getOutputStyles(id: number): Readonly<Record<string, IndicatorOutputAppearance>> | null {
        const entry = this._indicators.find(candidate => candidate.id === id);
        return entry ? captureIndicatorOutputStyles(entry) : null;
    }

    /** Restores a complete painter-style snapshot, including clearing newer fields. */
    replaceStyles(id: number, styles: Readonly<Record<string, unknown>>): boolean {
        const entry = this._indicators.find(candidate => candidate.id === id);
        if (!entry) return false;
        const skipped = replaceIndicatorStyles(entry, styles);
        if (skipped.length > 0)
            throw new Error(`sschart: unavailable indicator styles: ${skipped.join(', ')}`);
        this._emitChange();
        return true;
    }

    // Called by chart-legend on crosshair hover. A supplied `time` means the
    // value must belong to that exact candle. Carrying the previous value
    // forward is wrong for sparse studies (Fractals, pivots, signals): it makes
    // the legend describe a marker from another bar. Without a hover time we
    // still show the most recently formed value.
    getValuesAt(
        time: string | number | null | undefined,
        seriesData?: IndicatorSeriesDataLookup,
    ): IndicatorLegendRow[] {
        const result: IndicatorLegendRow[] = [];
        for (const entry of this._indicators) {
            if (entry.visible === false) continue;
            const fromSeries = seriesData?.get && Object.keys(entry.legendSources || {}).length > 0;
            const values = fromSeries
                ? this._pickValuesFromSeriesData(entry, seriesData)
                : this._pickValues(entry, time);
            if (!values) continue;
            const settings = IndicatorSettings.getIndicator(entry.type);
            const baseName = settings ? settings.name : entry.type;
            // Append the param tuple — "SMA(20)" matches the price-scale tag on
            // the chart so the legend line maps cleanly to the series axis label.
            const params = entry.params && Object.values(entry.params);
            const name = (params && params.length > 0) ? `${baseName}(${params.join(',')})` : baseName;
            result.push({
                id: entry.id,
                type: entry.type,
                name,
                values,
                colors: this._visibleOutputColors(entry),
                // paneId=null → overlay on the main chart; anything else is in
                // a sub-pane. Legend consumers can filter to avoid listing a
                // sub-pane indicator's values inside the main price-chart legend.
                paneId: entry.paneId,
            });
        }
        return result;
    }

    _pickValuesFromSeriesData(
        entry: IndicatorEntry,
        seriesData: IndicatorSeriesDataLookup,
    ): Record<string, number | null> | null {
        const values: Record<string, number | null> = {};
        const keys = Array.isArray(entry.outputNames) && entry.outputNames.length > 0
            ? entry.outputNames
            : Object.keys(entry.legendSources || {});
        const visibleKeys = keys.filter(key => indicatorOutputVisible(entry, key));
        for (const key of visibleKeys) {
            const source = entry.legendSources?.[key];
            const point = source ? seriesData.get(source.series) : null;
            // Repeating the `source` test is what carries its narrowing to the field lookup;
            // without a source there is no point either, so the branch is unchanged. A point
            // that is not an object could never have carried the field anyway.
            const raw = source && indexableRecord(point) ? point[source.field || 'value'] : null;
            const numeric = raw == null ? NaN : Number(raw);
            values[key] = Number.isFinite(numeric) ? numeric : null;
        }
        return visibleKeys.length > 0 ? values : null;
    }

    _pickValues(entry: IndicatorEntry, time: string | number | null | undefined) {
        if (time == null) return this._completeLegendValues(entry, entry._lastValues);

        const arr = entry._points || [];
        const target = this._toSec(time);
        // Binary search for this exact candle. Continuous indicators normally
        // have a point on every formed bar; sparse/shifted indicators do not.
        let lo = 0, hi = arr.length - 1;
        while (lo <= hi) {
            const mid = (lo + hi) >> 1;
            if (arr[mid].time === target) {
                return this._completeLegendValues(entry, arr[mid].values);
            }
            if (arr[mid].time < target) lo = mid + 1;
            else hi = mid - 1;
        }

        // Keep the output schema stable even when this candle has no value.
        // In particular Fractals alternates between `{up}` and `{down}` points;
        // returning both keys keeps legend DOM and painter colours aligned.
        return this._completeLegendValues(entry, null);
    }

    _completeLegendValues(
        entry: IndicatorEntry,
        values: LegendValues | null | undefined,
    ): Record<string, number | null> | null {
        const keys = Array.isArray(entry.outputNames) && entry.outputNames.length > 0
            ? entry.outputNames
            : Object.keys(values || {});
        const visibleKeys = keys.filter(key => indicatorOutputVisible(entry, key));
        if (visibleKeys.length === 0) return null;

        const complete: Record<string, number | null> = {};
        for (const key of visibleKeys) {
            const value = values && values[key];
            complete[key] = value == null ? null : Number(value);
        }
        return complete;
    }

    _visibleOutputColors(entry: IndicatorEntry): string[] {
        const outputs = Array.isArray(entry.outputNames) ? entry.outputNames : [];
        return outputs.reduce((colors, outputId, index) => {
            if (indicatorOutputVisible(entry, outputId)) colors.push(entry.colors?.[index]);
            return colors;
        }, [] as string[]);
    }


    _shiftTime(rawTime: number, shift: number | undefined): number {
        if (!shift || shift <= 0 || !this._candles.length) return rawTime;
        // Walk the current candle array backwards N bars from rawTime. If we
        // can't find rawTime in the array, fall back to timeframe-based maths.
        const idx = this._candles.findIndex(c => {
            const t = typeof c.time === 'number' ? c.time : Math.floor(new Date(c.time).getTime() / 1000);
            return t === rawTime;
        });
        if (idx >= shift) {
            const c = this._candles[idx - shift];
            return typeof c.time === 'number' ? c.time : Math.floor(new Date(c.time).getTime() / 1000);
        }
        // Fallback: subtract N * timeframe-seconds derived from the last two bars.
        if (this._candles.length >= 2) {
            const a = this._candles[this._candles.length - 2];
            const b = this._candles[this._candles.length - 1];
            const ta = typeof a.time === 'number' ? a.time : Math.floor(new Date(a.time).getTime() / 1000);
            const tb = typeof b.time === 'number' ? b.time : Math.floor(new Date(b.time).getTime() / 1000);
            const tfSec = Math.max(1, tb - ta);
            return rawTime - shift * tfSec;
        }
        return rawTime;
    }

    _applyPointShifts(
        data: ShiftablePoint[] | Record<string, ShiftablePoint[]>,
    ): ShiftablePoint[] | Record<string, ShiftablePoint[]> {
        const shiftPoint = (point: ShiftablePoint) => {
            const shift = Number(point?.shift) || 0;
            if (shift <= 0) return point;
            return { ...point, time: this._shiftTime(this._toSec(point.time), shift) };
        };

        if (Array.isArray(data)) return data.map(shiftPoint);
        if (data && typeof data === 'object') {
            const shifted: Record<string, ShiftablePoint[]> = {};
            for (const key of Object.keys(data)) {
                shifted[key] = Array.isArray(data[key]) ? data[key].map(shiftPoint) : data[key];
            }
            return shifted;
        }
        return data;
    }

    _mergeParams(
        settings: IndicatorCatalogEntry,
        params: IndicatorParams | null,
        definition: IndicatorDefinition,
    ): IndicatorParams {
        const canonical = migrateIndicatorParameters(definition, params);
        const merged: IndicatorParams = {};
        settings.params.forEach(p => {
            merged[p.key] = canonical[p.key] !== undefined ? canonical[p.key] : p.default;
        });
        return merged;
    }

    _formatParams(params: IndicatorParams): string {
        return Object.values(params).join(', ');
    }

    /// <summary>
    /// Picks an existing sub-pane that shares this indicator's natural Y-axis
    /// scale, or creates a new one. Two RSIs, or RSI + Stochastic, both land
    /// in the same 0..100 pane; a CCI (-1..+1) opens its own. When the
    /// catalog has no measure (legacy data path), falls back to opening a
    /// fresh pane per indicator — the old behaviour.
    /// </summary>
    _resolveSubPane(settings: IndicatorCatalogEntry, mergedParams: IndicatorParams): string | null {
        if (!this._paneManager) return null;

        const label = settings.name + ' (' + this._formatParams(mergedParams) + ')';
        const measure = settings.measure;

        if (measure && measure !== 'Price') {
            const existing = this._paneManager.getPaneByMeasure(measure);
            if (existing) return existing;
        }

        return this._paneManager.addPane(label, measure || null);
    }

}
